import { ChevronRightIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';
import type { ReactNode } from 'react';

import { OrgLink } from '@atoms/OrgLink';
import { Spinner } from '@atoms/Spinner/Spinner';

import type { NormalizedStatus } from './utils/types';

type Props = {
  ariaLabel: string;
  titleText: string;
  headerLinkTo: string;
  totalCount: number;
  statusItems: NormalizedStatus[];
  onBadgeClick: (code?: string) => void;
  testIdPrefix?: string;
  // maybe we can swap with the badge colors at some point, but they don't match design fully:
  dotColorMap: Record<string, string>;
  loading: boolean;
  error?: unknown | null;
  errorMessage?: string;
  footer?: ReactNode | null;
};

export function StatusWidget({
  ariaLabel,
  titleText,
  headerLinkTo,
  totalCount,
  statusItems,
  onBadgeClick,
  testIdPrefix = 'status-widget-row-',
  dotColorMap,
  loading,
  error,
  errorMessage = 'Something went wrong',
  footer = null
}: Props) {
  return (
    <section
      aria-label={ariaLabel}
      className="cool-shadow w-full rounded-2xl border bg-white p-4"
    >
      <div className="flex items-baseline">
        <OrgLink
          to={headerLinkTo}
          className="space-x-2 text-lg font-semibold leading-none hover:text-content-brand"
        >
          <span>{titleText}</span>
          <span>({totalCount})</span>
        </OrgLink>
      </div>

      <span className="text-sm font-normal text-content-secondary">
        Past 30 days
      </span>

      {loading ? (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      ) : (
        <ul role="list" className="mt-4 flex flex-col gap-2">
          {statusItems.map((status) => (
            <li key={status.code}>
              <button
                type="button"
                onClick={() => onBadgeClick(status.code)}
                data-testid={`${testIdPrefix}${status.code}`}
                className="group w-full px-2 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-md w-4 shrink-0 text-right font-semibold tabular-nums">
                    {status.count}
                  </span>

                  <div className="flex min-w-0 items-center gap-2 pl-6">
                    <span
                      className={cn(
                        'h-3 w-3 shrink-0 rounded-full',
                        dotColorMap[status.code]
                      )}
                      aria-hidden
                    />

                    <span className="text-md min-w-0 break-words font-light leading-snug">
                      {status.name}
                    </span>
                  </div>

                  <div className="ml-1 flex items-center">
                    <ChevronRightIcon className="invisible size-3 stroke-2 group-hover:visible group-hover:stroke-content-secondary" />
                  </div>
                </div>
              </button>
            </li>
          ))}

          {!!error && (
            <li>
              <p className="text-sm text-red-600">{errorMessage}</p>
            </li>
          )}
        </ul>
      )}

      {footer}
    </section>
  );
}
